import * as React from 'react';
import { Bot, AlertCircle, Power, Wrench, Activity } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './Card';
import { Badge } from './Badge';
import { cn } from '../utils/cn';
import { getRelativeTime, isWithinMinutes } from '../utils';
import type { Agent, AgentStatus } from '../types';

export interface AgentCardProps {
  agent: Agent;
  onClick?: () => void;
  className?: string;
}

const statusConfig: Record<
  AgentStatus,
  {
    icon: React.ReactNode;
    variant: 'success' | 'destructive' | 'warning' | 'secondary';
    label: string;
    description: string;
  }
> = {
  ACTIVE: {
    icon: <Power className="h-3.5 w-3.5" />,
    variant: 'success',
    label: 'Active',
    description: 'Ready to receive dispatches',
  },
  INACTIVE: {
    icon: <Power className="h-3.5 w-3.5" />,
    variant: 'secondary',
    label: 'Inactive',
    description: 'Not accepting dispatches',
  },
  ERROR: {
    icon: <AlertCircle className="h-3.5 w-3.5" />,
    variant: 'destructive',
    label: 'Error',
    description: 'Agent encountered an error',
  },
  MAINTENANCE: {
    icon: <Wrench className="h-3.5 w-3.5" />,
    variant: 'warning',
    label: 'Maintenance',
    description: 'Undergoing maintenance',
  },
};

export const AgentCard = React.memo<AgentCardProps>(
  ({ agent, onClick, className }) => {
    const config = statusConfig[agent.status];
    const isHealthy = agent.last_heartbeat && isWithinMinutes(agent.last_heartbeat, 5);
    const heartbeatStatus = agent.status === 'ACTIVE' && !isHealthy ? 'warning' : undefined;

    return (
      <Card
        className={cn(
          'transition-all hover:shadow-md',
          onClick && 'cursor-pointer hover:border-primary/50',
          className
        )}
        onClick={onClick}
      >
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className="rounded-lg bg-primary/10 p-2">
                <Bot className="h-5 w-5 text-primary" />
              </div>
              <div className="space-y-1">
                <CardTitle className="text-base">{agent.name}</CardTitle>
                <CardDescription className="text-xs">{agent.type}</CardDescription>
              </div>
            </div>
            <div className="flex flex-col items-end gap-1">
              <Badge variant={heartbeatStatus || config.variant} className="flex items-center gap-1">
                {config.icon}
                <span>{config.label}</span>
              </Badge>
              {agent.last_heartbeat && (
                <div className="flex items-center gap-1">
                  <div
                    className={cn(
                      'h-2 w-2 rounded-full',
                      isHealthy ? 'bg-green-500 animate-pulse-subtle' : 'bg-yellow-500'
                    )}
                  />
                  <span className="text-xs text-muted-foreground">
                    {getRelativeTime(agent.last_heartbeat)}
                  </span>
                </div>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {agent.description && (
              <p className="text-sm text-muted-foreground">{agent.description}</p>
            )}
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>Policy Version</span>
              <span className="font-mono font-medium text-foreground">
                {agent.policy_version || 'N/A'}
              </span>
            </div>
            {agent.capabilities && agent.capabilities.length > 0 && (
              <div className="space-y-1">
                <span className="text-xs text-muted-foreground">Capabilities</span>
                <div className="flex flex-wrap gap-1">
                  {agent.capabilities.slice(0, 3).map((cap) => (
                    <Badge key={cap} variant="outline" className="text-xs">
                      {cap}
                    </Badge>
                  ))}
                  {agent.capabilities.length > 3 && (
                    <Badge variant="outline" className="text-xs">
                      +{agent.capabilities.length - 3} more
                    </Badge>
                  )}
                </div>
              </div>
            )}
            {agent.current_session && (
              <div className="mt-2 rounded-md bg-blue-500/10 border border-blue-500/20 p-2 text-xs">
                <div className="flex items-center gap-2 text-blue-700 dark:text-blue-300">
                  <Activity className="h-3 w-3 animate-pulse" />
                  <span>Currently executing session</span>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }
);

AgentCard.displayName = 'AgentCard';
